/**
 * WalletPass.jsx — pixel-faithful Apple Wallet generic pass
 * ---------------------------------------------------------
 * Mirrors the EXACT layout Apple renders so the landing/dashboard
 * preview matches what the user sees in Wallet. QR sits small +
 * centered at the bottom, exactly like a real generic pass.
 *
 * Real generic pass structure:
 *   header row:  logo (left)            ·  header fields (right)
 *   primary:     primary field          ·  thumbnail (right)
 *   secondary:   secondary fields row
 *   auxiliary:   auxiliary fields row
 *   barcode:     QR, centered, on white, with alt text
 */

import React from 'react';

export default function WalletPass({ card, logoUrl, photoUrl, side = 'front', scale = 1, photoRadius = 6 }) {
  const bg = card?.bg_color || '#1c1c20';
  const fg = card?.fg_color || '#FFFFFF';
  const label = card?.label_color || 'rgba(255,255,255,0.55)';

  return (
    <div className="flip-container" dir="ltr" style={{ width: 320 * scale, height: 404 * scale }}>
      <div className={`flip-card ${side === 'back' ? 'flipped' : ''}`} style={{ width: '100%', height: '100%' }}>
        <div className="flip-face" style={{ width: '100%', height: '100%' }}>
          <Shell bg={bg} scale={scale}>
            <Front card={card} fg={fg} label={label} logoUrl={logoUrl} photoUrl={photoUrl} scale={scale} photoRadius={photoRadius} />
          </Shell>
        </div>
        <div className="flip-face flip-face-back" style={{ width: '100%', height: '100%' }}>
          <Shell bg={bg} scale={scale}>
            <Back card={card} fg={fg} label={label} scale={scale} />
          </Shell>
        </div>
      </div>
    </div>
  );
}

function Shell({ bg, scale, children }) {
  return (
    <div style={{
      width: '100%', height: '100%', background: bg, borderRadius: 14 * scale,
      padding: `${14 * scale}px ${15 * scale}px`, display: 'flex', flexDirection: 'column',
      boxShadow: `0 ${20 * scale}px ${50 * scale}px -${12 * scale}px rgba(0,0,0,0.55), inset 0 0.5px 0 rgba(255,255,255,0.12)`,
      overflow: 'hidden', position: 'relative',
      transition: 'background-color var(--dur-normal) ease'
    }}>
      {children}
    </div>
  );
}

/* ─── FRONT ───────────────────────────────────────────────── */
function Front({ card, fg, label, logoUrl, photoUrl, scale, photoRadius = 6 }) {
  const S = (n) => n * scale;
  const lbl = (txt) => (
    <div style={{ fontSize: S(8.5), fontWeight: 600, letterSpacing: '0.06em', color: label, textTransform: 'uppercase', lineHeight: 1.2 }}>{txt}</div>
  );
  const val = (txt, size = 13) => (
    <div style={{ fontSize: S(size), color: fg, lineHeight: 1.25, marginTop: S(1), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{txt}</div>
  );

  return (
    <>
      {/* Header row: logo left + serial right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: S(28) }}>
        {logoUrl
          ? <img src={logoUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ height: S(24), maxWidth: S(130), objectFit: 'contain' }} />
          : <div style={{ fontSize: S(15), fontWeight: 600, color: fg, letterSpacing: '-0.01em' }}>{card?.company || 'Company'}</div>}
        <div style={{ textAlign: 'right' }}>
          {lbl('ID')}
          <div dir="ltr" style={{ fontSize: S(11), color: fg, fontFamily: "'Thmanyah Sans', monospace", lineHeight: 1.2 }}>{card?.serial_number || 'MC-0001'}</div>
        </div>
      </div>

      {/* Primary field + thumbnail */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: S(10), marginTop: S(14) }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {lbl('NAME')}
          <div style={{ fontSize: S(21), fontWeight: 500, color: fg, lineHeight: 1.1, marginTop: S(2), letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {card?.full_name_en || 'Your Name'}
          </div>
        </div>
        {photoUrl && (
          <img src={photoUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: S(46), height: S(46), borderRadius: S(photoRadius), objectFit: 'cover', flexShrink: 0 }} />
        )}
      </div>

      {/* Secondary fields: title + company */}
      <div style={{ display: 'flex', gap: S(14), marginTop: S(13) }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {lbl('TITLE')}
          {val(card?.title_en || 'Your Title', 12)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {lbl('COMPANY')}
          {val(card?.company || 'Company', 12)}
        </div>
      </div>

      {/* Auxiliary fields: phone + email */}
      <div style={{ display: 'flex', gap: S(14), marginTop: S(11) }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {lbl('PHONE')}
          {val(card?.phone || '+966 5X XXX XXXX', 11)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {lbl('EMAIL')}
          {val(card?.email || 'you@email.com', 11)}
        </div>
      </div>

      {/* Barcode — small, centered, on white, at the bottom (real Wallet) */}
      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: S(12) }}>
        <div style={{ background: 'white', borderRadius: S(6), padding: S(6), width: S(78), height: S(78) }}>
          <FakeQR />
        </div>
        <div style={{ fontSize: S(8), color: label, marginTop: S(4) }}>
          {card?.qr_mode === 'url' ? 'Scan to open my page' : 'Scan to save contact'}
        </div>
      </div>
    </>
  );
}

/* ─── BACK ────────────────────────────────────────────────── */
function Back({ card, fg, label, scale }) {
  const S = (n) => n * scale;
  const rows = [
    { l: 'الاسم', v: card?.full_name_ar, rtl: true },
    { l: 'ABOUT', v: card?.about_en },
    { l: 'LINKEDIN', v: card?.linkedin_url, link: true },
    { l: 'X', v: card?.x_url, link: true },
    { l: 'WEBSITE', v: card?.website || 'engmohammed.net', link: true },
    { l: 'OFFICE', v: card?.office_location },
    { l: 'CREDIT', v: 'تصميم وتطوير المهندس محمد الزهراني', rtl: true }
  ].filter((r) => r.v);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: S(10), borderBottom: `0.5px solid ${label}` }}>
        <div style={{ fontSize: S(13), fontWeight: 600, color: fg }}>{card?.company || 'Company'}</div>
        <div style={{ fontSize: S(10), color: label, fontFamily: "'Thmanyah Sans', monospace" }}>{card?.serial_number || 'MC-0001'}</div>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {rows.map((r, i) => (
          <div key={i} style={{ padding: `${S(8)}px 0`, borderBottom: `0.5px solid ${label}33` }} dir={r.rtl ? 'rtl' : 'ltr'}>
            <div style={{ fontSize: S(8.5), fontWeight: 600, letterSpacing: r.rtl ? 0 : '0.06em', color: label, textTransform: r.rtl ? 'none' : 'uppercase' }}>{r.l}</div>
            <div style={{ fontSize: S(11), color: r.link ? '#5B9CFF' : fg, marginTop: S(2), lineHeight: 1.4, wordBreak: 'break-word' }}>{r.v}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: S(8), color: label, textAlign: 'center', marginTop: S(8) }}>engmohammed.net</div>
    </>
  );
}

export function FakeQR() {
  return (
    <svg viewBox="0 0 21 21" style={{ width: '100%', height: '100%', display: 'block' }} shapeRendering="crispEdges">
      <g fill="#000">
        <path d="M0 0h7v1H0zM0 6h7v1H0zM0 0h1v7H0zM6 0h1v7H6zM2 2h3v3H2z" />
        <path d="M14 0h7v1h-7zM14 6h7v1h-7zM14 0h1v7h-1zM20 0h1v7h-1zM16 2h3v3h-3z" />
        <path d="M0 14h7v1H0zM0 20h7v1H0zM0 14h1v7H0zM6 14h1v7H6zM2 16h3v3h-3z" />
        {[[8,0],[11,0],[13,0],[9,1],[12,1],[8,2],[10,2],[11,2],[9,3],[13,3],[8,4],[11,4],[12,4],[9,5],[10,5],[13,5],[8,6],[10,6],[12,6],[0,8],[2,8],[3,8],[5,8],[7,8],[9,8],[11,8],[14,8],[16,8],[18,8],[20,8],[1,9],[4,9],[7,9],[10,9],[13,9],[15,9],[17,9],[19,9],[0,10],[3,10],[6,10],[8,10],[11,10],[14,10],[16,10],[20,10],[1,11],[2,11],[5,11],[9,11],[12,11],[15,11],[18,11],[4,12],[7,12],[10,12],[13,12],[14,12],[16,12],[17,12],[19,12],[0,13],[3,13],[6,13],[8,13],[11,13],[12,13],[15,13],[18,13],[20,13],[8,14],[9,14],[11,14],[14,14],[16,14],[19,14],[8,15],[10,15],[13,15],[15,15],[18,15],[20,15],[9,16],[12,16],[14,16],[17,16],[8,17],[11,17],[13,17],[16,17],[19,17],[10,18],[12,18],[15,18],[18,18],[20,18],[9,19],[11,19],[14,19],[17,19],[8,20],[13,20],[15,20],[16,20],[19,20]].map(([x,y]) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" />
        ))}
      </g>
    </svg>
  );
}
